import { useEffect, useState, useMemo, useRef } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import PageContainer from "../../../components/container/PageContainer";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import EditIcon from "@mui/icons-material/Edit";

import { useFormik } from "formik";
import * as Yup from "yup";

import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";

import { GetAllEmployeeDetailsDropdowns } from "../../../services/human-resource";
import {
  AddOrEditLeaveBalance,
  ImportLeaveBalanceYearly,
} from "../../../services/asaiadmin";

/* =========================================================
   MONTH AND YEAR OPTIONS
========================================================= */

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CURRENT_YEAR = new Date().getFullYear();

const YEARS = Array.from(
  { length: 11 },
  (_, index) => CURRENT_YEAR - 5 + index,
);

const LEAVE_TYPES = ["EL", "CL", "SL"];

// The API currently accepts up to 5,000 rows per request. Larger workbooks
// are submitted as sequential batches so HR can import any practical row count.
const IMPORT_BATCH_SIZE = 5000;

/* =========================================================
   FORM BALANCE GROUPS
========================================================= */

const BALANCE_GROUPS = [
  {
    title: "Opening Balance",
    prefix: "opening",
  },
  {
    title: "Used Balance",
    prefix: "used",
  },
  {
    title: "Closing Balance",
    prefix: "closing",
  },
];

const BALANCE_COLUMNS = BALANCE_GROUPS.flatMap((group) =>
  LEAVE_TYPES.map((leaveType) => ({
    label: `${group.title.replace(" Balance", "")} ${leaveType}`,
    field: `${group.prefix}${leaveType}`,
  })),
);

// Keep this order aligned with the table header.
const FILTER_FIELDS = [
  "employeeId",
  "employeeName",
  "month",
  "year",
  ...BALANCE_COLUMNS.map((column) => column.field),
];

/* =========================================================
   EMPTY FORM
========================================================= */

const getEmptyForm = () => ({
  employeeId: "",
  month: MONTHS[new Date().getMonth()],
  year: String(CURRENT_YEAR),

  openingEL: "",
  openingCL: "",
  openingSL: "",

  usedEL: "0",
  usedCL: "0",
  usedSL: "0",
});

/* =========================================================
   NORMALIZE FILTER TEXT
========================================================= */

const normalizeFilterValue = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\s+/g, "");

/* =========================================================
   CALCULATE CLOSING BALANCE
========================================================= */

const calculateClosingBalance = (opening, used) => {
  if (
    opening === "" ||
    opening === null ||
    opening === undefined ||
    used === "" ||
    used === null ||
    used === undefined
  ) {
    return "";
  }

  const openingValue = Number(opening);
  const usedValue = Number(used);

  if (!Number.isFinite(openingValue) || !Number.isFinite(usedValue)) {
    return "";
  }

  return openingValue - usedValue;
};

/* =========================================================
   FORMAT BALANCE
========================================================= */

const formatBalance = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  return Number.isFinite(number)
    ? number.toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      })
    : "-";
};

/* =========================================================
   MAP EMPLOYEE RESPONSE
========================================================= */

const mapEmployee = (employee) => ({
  id: String(
    employee?.personID ??
      employee?.PersonID ??
      employee?.personId ??
      employee?.PersonId ??
      employee?.userID ??
      employee?.UserID ??
      employee?.empId ??
      employee?.EmpId ??
      employee?.id ??
      employee?.Id ??
      "",
  ),

  name: String(
    employee?.buddyName ??
      employee?.employeeName ??
      employee?.EmployeeName ??
      employee?.empName ??
      employee?.EmpName ??
      employee?.name ??
      employee?.Name ??
      "",
  ).trim(),

  organization: String(
    employee?.organization ?? employee?.Organization ?? "",
  ).trim(),
});

/* =========================================================
   PERIOD HELPERS
========================================================= */

const getPreviousPeriod = (month, year) => {
  const monthIndex = MONTHS.indexOf(month);

  if (monthIndex <= 0 || !year) {
    // January is initialized by the yearly import; December is never carried
    // into the new year because remaining leave is settled separately.
    return null;
  }

  const date = new Date(Number(year), monthIndex, 1);
  date.setMonth(date.getMonth() - 1);

  return {
    month: MONTHS[date.getMonth()],
    year: date.getFullYear(),
  };
};

/* =========================================================
   COMPONENT
========================================================= */

const LeaveBalanceMaster = () => {
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");

  const [list, setList] = useState([]);
  const [editId, setEditId] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const [globalSearch, setGlobalSearch] = useState("");
  const [searchText, setSearchText] = useState({});
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState(false);

  const [isImporting, setIsImporting] = useState(false);

  const yearlyImportFileInputRef = useRef(null);
  const importInProgressRef = useRef(false);

  const [importConfirmation, setImportConfirmation] = useState({
    open: false,
    year: CURRENT_YEAR,
    rowCount: 0,
  });
  const importConfirmationResolverRef = useRef(null);

  const requestImportConfirmation = (year, rowCount) =>
    new Promise((resolve) => {
      importConfirmationResolverRef.current = resolve;
      setImportConfirmation({ open: true, year, rowCount });
    });

  const resolveImportConfirmation = (confirmed) => {
    setImportConfirmation((current) => ({ ...current, open: false }));

    const resolve = importConfirmationResolverRef.current;
    importConfirmationResolverRef.current = null;
    resolve?.(confirmed);
  };

  /* =======================================================
     BREADCRUMB
  ======================================================= */

  const BCrumb = [
    {
      to: "/",
      title: "ASAI Admin",
    },
    {
      title: "Leave Balance Master",
    },
  ];

  /* =======================================================
     FETCH LEAVE BALANCES
  ======================================================= */

  const fetchLeaveBalances = async (employeeList = employees) => {
    try {
      setBalancesLoading(true);

      const response = await AddOrEditLeaveBalance({
        action: "GET",
      });

      const responseData = response?.data;

      const rows = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData?.result)
        ? responseData.result
        : [];

      const mappedRecords = rows.map((row) => {
        const employeeId = String(row.employeeId ?? row.EmployeeId ?? "");

        const rawMonth = row.month ?? row.Month;

        const monthNumber = Number(
          row.monthNumber ?? row.MonthNumber ?? rawMonth,
        );

        const normalizedMonth =
          MONTHS.find(
            (month) =>
              month.toLowerCase() === String(rawMonth ?? "").toLowerCase(),
          ) ??
          (monthNumber >= 1 && monthNumber <= 12
            ? MONTHS[monthNumber - 1]
            : "");

        const employee = employeeList.find(
          (item) => String(item.id) === employeeId,
        );

        return {
          id: Number(row.id ?? row.Id),
          employeeId,
          employeeName:
            row.employeeName ??
            row.EmployeeName ??
            employee?.name ??
            `Employee ${employeeId}`,

          month: normalizedMonth,

          year: Number(
            row.year ?? row.Year ?? row.balanceYear ?? row.BalanceYear,
          ),

          openingEL: Number(row.openingEL ?? row.OpeningEL ?? 0),
          openingCL: Number(row.openingCL ?? row.OpeningCL ?? 0),
          openingSL: Number(row.openingSL ?? row.OpeningSL ?? 0),

          usedEL: Number(row.usedEL ?? row.UsedEL ?? 0),
          usedCL: Number(row.usedCL ?? row.UsedCL ?? 0),
          usedSL: Number(row.usedSL ?? row.UsedSL ?? 0),

          closingEL: Number(row.closingEL ?? row.ClosingEL ?? 0),
          closingCL: Number(row.closingCL ?? row.ClosingCL ?? 0),
          closingSL: Number(row.closingSL ?? row.ClosingSL ?? 0),
        };
      });

      setList(mappedRecords);
    } catch (error) {
      console.error("Failed to load leave balances:", error);

      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.Message ||
          error?.response?.data?.detail ||
          "Failed to load leave balances",
      );
    } finally {
      setBalancesLoading(false);
    }
  };

  /* =======================================================
     FETCH EMPLOYEES
  ======================================================= */

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);

      const response = await GetAllEmployeeDetailsDropdowns(true);
      const data = response?.data;

      const employeeData =
        data?.buddyDropDown ??
        data?.data?.buddyDropDown ??
        data?.result?.buddyDropDown ??
        [];

      const mappedEmployees = Array.isArray(employeeData)
        ? employeeData
            .map(mapEmployee)
            .filter((employee) => employee.id && employee.name)
        : [];

      const uniqueEmployees = Array.from(
        new Map(
          mappedEmployees.map((employee) => [employee.id, employee]),
        ).values(),
      );

      setEmployees(uniqueEmployees);

      await fetchLeaveBalances(uniqueEmployees);
    } catch (error) {
      console.error("Failed to load employees:", error);

      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.Message ||
          "Failed to load employees",
      );
    } finally {
      setEmployeesLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  /* =======================================================
     EMPLOYEE DROPDOWN SEARCH
  ======================================================= */

  const filteredEmployees = useMemo(() => {
    const keyword = employeeSearchTerm.trim().toLowerCase();

    if (!keyword) {
      return employees;
    }

    return employees.filter((employee) => {
      const nameMatches = employee.name.toLowerCase().includes(keyword);
      const idMatches = employee.id.toLowerCase().includes(keyword);

      return nameMatches || idMatches;
    });
  }, [employees, employeeSearchTerm]);

  const getSelectedEmployee = (employeeId) =>
    employees.find((employee) => String(employee.id) === String(employeeId));

  /* =======================================================
     FORMIK VALIDATION
  ======================================================= */

  const validationSchema = Yup.object({
    employeeId: Yup.string().required("Employee name is required"),

    month: Yup.string().required("Month is required"),

    year: Yup.string()
      .required("Year is required")
      .matches(/^\d{4}$/, "Enter a valid year"),

    openingEL: Yup.number()
      .typeError("Required")
      .min(0, "Cannot be negative")
      .required("Required"),

    openingCL: Yup.number()
      .typeError("Required")
      .min(0, "Cannot be negative")
      .required("Required"),

    openingSL: Yup.number()
      .typeError("Required")
      .min(0, "Cannot be negative")
      .required("Required"),

    usedEL: Yup.number()
      .typeError("Required")
      .min(0, "Cannot be negative")
      .max(Yup.ref("openingEL"), "Used EL cannot exceed available EL")
      .required("Required"),

    usedCL: Yup.number()
      .typeError("Required")
      .min(0, "Cannot be negative")
      .max(Yup.ref("openingCL"), "Used CL cannot exceed available CL")
      .required("Required"),

    usedSL: Yup.number()
      .typeError("Required")
      .min(0, "Cannot be negative")
      .max(Yup.ref("openingSL"), "Used SL cannot exceed available SL")
      .required("Required"),
  });

  const formik = useFormik({
    initialValues: getEmptyForm(),
    validationSchema,

    onSubmit: async (values, { resetForm }) => {
      try {
        const employeeId = Number(values.employeeId);
        const selectedEmployee = getSelectedEmployee(values.employeeId);

        if (!Number.isInteger(employeeId) || employeeId <= 0) {
          toast.error("Please select a valid employee.");
          return;
        }

        // For a new record, require a valid dropdown selection.
        // Existing records can still be updated using their saved EmployeeId.
        if (editId === null && !selectedEmployee) {
          toast.error("Please select an employee from the dropdown.");
          return;
        }

        const employeeLabel = selectedEmployee
          ? `${selectedEmployee.name} (${selectedEmployee.id})`
          : `Employee ${employeeId}`;

        const duplicate = list.some(
          (item) =>
            Number(item.employeeId) === employeeId &&
            item.month === values.month &&
            Number(item.year) === Number(values.year) &&
            item.id !== editId,
        );

        if (duplicate) {
          toast.error(
            `Leave balance already exists for ${employeeLabel} - ${values.month} ${values.year}.`,
          );
          return;
        }

        const payload = {
          action: editId !== null ? "UPDATE" : "INSERT",

          ...(editId !== null ? { id: Number(editId) } : {}),

          employeeId,
          month: values.month,
          year: Number(values.year),

          usedEL: Number(values.usedEL),
          usedCL: Number(values.usedCL),
          usedSL: Number(values.usedSL),

          // Opening is only sent when creating a new record.
          // On UPDATE, SQL preserves the existing opening or
          // carries it forward from the previous month within the same year.
          ...(editId === null
            ? {
                openingEL: Number(values.openingEL),
                openingCL: Number(values.openingCL),
                openingSL: Number(values.openingSL),
              }
            : {}),
        };

        const response = await AddOrEditLeaveBalance(payload);

        const result = Array.isArray(response?.data)
          ? response.data[0]
          : response?.data?.data?.[0] ??
            response?.data?.result?.[0] ??
            response?.data ??
            {};

        const isEditing = editId !== null;

        // Reload from SQL to reflect calculated closing balances.
        await fetchLeaveBalances(employees);

        toast.success(
          result?.message ??
            result?.Message ??
            (isEditing
              ? "Leave balance updated successfully."
              : "Leave balance added successfully."),
        );

        resetForm({
          values: getEmptyForm(),
        });

        setEditId(null);
        setEmployeeSearchTerm("");
        setPage(0);
      } catch (error) {
        console.error("Failed to save leave balance:", error);

        const errorData = error?.response?.data;

        const validationMessage = errorData?.errors
          ? Object.values(errorData.errors).flat().join(" ")
          : "";

        toast.error(
          errorData?.message ||
            errorData?.Message ||
            errorData?.detail ||
            validationMessage ||
            error?.message ||
            "Failed to save leave balance.",
        );
      }
    },
  });

  /* =======================================================
     FIND PREVIOUS MONTH'S BALANCE
  ======================================================= */

  const previousPeriod = useMemo(
    () => getPreviousPeriod(formik.values.month, formik.values.year),
    [formik.values.month, formik.values.year],
  );

  const previousBalance = useMemo(() => {
    if (!formik.values.employeeId || !previousPeriod) {
      return null;
    }

    return (
      list.find(
        (record) =>
          String(record.employeeId) === String(formik.values.employeeId) &&
          record.month === previousPeriod.month &&
          Number(record.year) === previousPeriod.year,
      ) ?? null
    );
  }, [list, formik.values.employeeId, previousPeriod]);

  /* =======================================================
     AUTO-POPULATE OPENING FROM PREVIOUS CLOSING
  ======================================================= */

  useEffect(() => {
    if (
      !formik.values.employeeId ||
      !formik.values.month ||
      !formik.values.year
    ) {
      return;
    }

    if (previousBalance) {
      formik.setValues(
        (currentValues) => ({
          ...currentValues,
          openingEL: String(previousBalance.closingEL),
          openingCL: String(previousBalance.closingCL),
          openingSL: String(previousBalance.closingSL),
        }),
        false,
      );

      return;
    }

    if (editId === null) {
      formik.setValues(
        (currentValues) => ({
          ...currentValues,
          openingEL: "",
          openingCL: "",
          openingSL: "",
        }),
        false,
      );
    }
  }, [
    formik.values.employeeId,
    formik.values.month,
    formik.values.year,
    previousBalance?.id,
    previousBalance?.closingEL,
    previousBalance?.closingCL,
    previousBalance?.closingSL,
    editId,
    formik.setValues,
  ]);

  /* =======================================================
     EDIT RECORD
  ======================================================= */

  const handleEdit = (item) => {
    setEditId(item.id);
    setEmployeeSearchTerm("");

    formik.setValues({
      employeeId: String(item.employeeId),
      month: item.month,
      year: String(item.year),

      openingEL: String(item.openingEL),
      openingCL: String(item.openingCL),
      openingSL: String(item.openingSL),

      usedEL: String(item.usedEL),
      usedCL: String(item.usedCL),
      usedSL: String(item.usedSL),
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =======================================================
     RESET FORM
  ======================================================= */

  const resetForm = () => {
    setEditId(null);
    setEmployeeSearchTerm("");

    formik.resetForm({
      values: getEmptyForm(),
    });
  };

  const handleUsedBalanceChange = (event, leaveType) => {
    const field = `used${leaveType}`;
    const openingField = `opening${leaveType}`;

    const rawValue = event.target.value;
    const openingValue = Number(formik.values[openingField] || 0);
    const maxAllowed = Math.max(0, openingValue);

    // Allow clearing the input while editing.
    if (rawValue === "") {
      formik.setFieldValue(field, "");
      return;
    }

    const usedValue = Number(rawValue);

    if (!Number.isFinite(usedValue) || usedValue < 0) {
      return;
    }

    // Never allow used leave to exceed the opening balance.
    if (usedValue > maxAllowed) {
      formik.setFieldValue(field, String(maxAllowed));
      formik.setFieldTouched(field, true, false);
      return;
    }

    formik.setFieldValue(field, rawValue);
  };

  /* =======================================================
     BALANCE INPUT GROUP
  ======================================================= */

  const renderBalanceGroup = (group) => {
    const isOpening = group.prefix === "opening";
    const isUsed = group.prefix === "used";
    const isClosing = group.prefix === "closing";

    return (
      <Box
        key={group.prefix}
        sx={{
          minWidth: 0,
          height: "100%",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          p: 1,
          backgroundColor: "#FAFBFB",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            mb: 1.25,
          }}
        >
          {group.title}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            flexWrap: "nowrap",
          }}
        >
          {LEAVE_TYPES.map((leaveType) => {
            const field = `${group.prefix}${leaveType}`;

            const openingAmount = Number(
              formik.values[`opening${leaveType}`] || 0,
            );

            const hasError =
              formik.touched[field] && Boolean(formik.errors[field]);

            let value = formik.values[field];

            if (isClosing) {
              value = calculateClosingBalance(
                formik.values[`opening${leaveType}`],
                formik.values[`used${leaveType}`],
              );
            }

            const disabled =
              isClosing ||
              (isOpening && (Boolean(previousBalance) || editId !== null));

            return (
              <Box
                key={field}
                sx={{
                  minWidth: 0,
                  flex: "1 1 0",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  {leaveType}
                </Typography>

                <TextField
                  type="number"
                  name={field}
                  value={value}
                  onChange={
                    isOpening
                      ? formik.handleChange
                      : isUsed
                      ? (event) => handleUsedBalanceChange(event, leaveType)
                      : undefined
                  }
                  onBlur={isOpening || isUsed ? formik.handleBlur : undefined}
                  fullWidth
                  size="small"
                  disabled={disabled}
                  error={Boolean(hasError)}
                  inputProps={{
                    min: 0,
                    max: isUsed
                      ? Math.max(
                          0,
                          Number(formik.values[`opening${leaveType}`] || 0),
                        )
                      : undefined,
                    step: 0.25,
                    readOnly: isClosing,
                    "aria-label": `${group.title} ${leaveType}`,
                  }}
                  sx={{
                    backgroundColor: disabled ? "#F1F3F5" : "#FFFFFF",

                    "& .MuiInputBase-root": {
                      height: 36,
                      fontSize: 13,
                    },

                    "& .MuiInputBase-input": {
                      padding: "7px 8px",
                    },

                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#333333",
                    },
                  }}
                />

                {isUsed && (
                  <FormHelperText
                    sx={{
                      mx: 0,
                      mt: 0.25,
                      fontSize: 10,
                      lineHeight: 1.2,
                    }}
                  >
                    Available: {formatBalance(openingAmount)}
                  </FormHelperText>
                )}

                {hasError && !disabled && (
                  <FormHelperText
                    error
                    sx={{
                      mx: 0,
                      mt: 0.25,
                      fontSize: 10,
                      lineHeight: 1.1,
                    }}
                  >
                    {formik.errors[field]}
                  </FormHelperText>
                )}
              </Box>
            );
          })}
        </Box>

        {isOpening && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 1,
              lineHeight: 1.4,
            }}
          >
            {previousBalance
              ? `Auto-filled from ${previousBalance.month} ${previousBalance.year} closing balance.`
              : "No previous-month record found. Enter the starting balance."}
          </Typography>
        )}

        {isUsed && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 1,
              lineHeight: 1.4,
            }}
          >
            Enter the leave used during this month.
          </Typography>
        )}

        {isClosing && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 1,
              lineHeight: 1.4,
            }}
          >
            Calculated automatically: Opening − Used. Unused year-end balances
            are settled, not carried into January.
          </Typography>
        )}
      </Box>
    );
  };

  /* =======================================================
     FILTER EXISTING RECORDS
  ======================================================= */

  const filteredList = useMemo(() => {
    return list.filter((item) => {
      const searchableText = [
        item.employeeName,
        item.employeeId,
        item.month,
        item.year,
        ...BALANCE_COLUMNS.map((column) => item[column.field]),
      ].join(" ");

      const globalMatch = normalizeFilterValue(searchableText).includes(
        normalizeFilterValue(globalSearch),
      );

      const columnMatch = Object.keys(searchText).every((key) => {
        if (!searchText[key]) {
          return true;
        }

        return normalizeFilterValue(item[key]).includes(
          normalizeFilterValue(searchText[key]),
        );
      });

      return globalMatch && columnMatch;
    });
  }, [list, globalSearch, searchText]);

  /* =======================================================
     SORTING
  ======================================================= */

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      if (!orderBy) {
        return 0;
      }

      let comparison = 0;

      if (
        orderBy === "year" ||
        BALANCE_COLUMNS.some((column) => column.field === orderBy)
      ) {
        comparison = Number(a[orderBy] ?? 0) - Number(b[orderBy] ?? 0);
      } else if (orderBy === "month") {
        comparison = MONTHS.indexOf(a.month) - MONTHS.indexOf(b.month);
      } else {
        comparison = String(a[orderBy] ?? "").localeCompare(
          String(b[orderBy] ?? ""),
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          },
        );
      }

      return order === "asc" ? comparison : -comparison;
    });
  }, [filteredList, orderBy, order]);

  /* =======================================================
     PAGINATION
  ======================================================= */

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredList.length / rowsPerPage) - 1,
    );

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredList.length, rowsPerPage, page]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /* =======================================================
     RESET FILTERS
  ======================================================= */

  const handleResetFilters = () => {
    setGlobalSearch("");
    setSearchText({});
    setShowAllFilters(false);
    setPage(0);
  };

  /* =======================================================
     EXPORT EXCEL
  ======================================================= */

  const handleExportExcel = () => {
    try {
      // Export all filtered records, not only the current page.
      const exportRows = sortedList.map((item) => ({
        "Employee ID": item.employeeId,
        "Employee Name": item.employeeName,
        Month: item.month,
        Year: item.year,

        "Opening EL": item.openingEL,
        "Opening CL": item.openingCL,
        "Opening SL": item.openingSL,

        "Used EL": item.usedEL,
        "Used CL": item.usedCL,
        "Used SL": item.usedSL,

        "Closing EL": item.closingEL,
        "Closing CL": item.closingCL,
        "Closing SL": item.closingSL,
      }));

      if (exportRows.length === 0) {
        toast.info("No records available to export.");
        return;
      }

      const hasActiveFilters =
        globalSearch.trim() !== "" ||
        Object.values(searchText).some(
          (value) => String(value ?? "").trim() !== "",
        );

      const worksheet = XLSX.utils.json_to_sheet(exportRows);

      worksheet["!cols"] = [
        { wch: 14 },
        { wch: 28 },
        { wch: 14 },
        { wch: 10 },
        ...Array(9).fill({ wch: 14 }),
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Leave Balances");

      const fileName = hasActiveFilters
        ? "Leave_Balance_Master_Filtered.xlsx"
        : "Leave_Balance_Master_All.xlsx";

      XLSX.writeFile(workbook, fileName);

      toast.success(
        `Exported ${exportRows.length} ${
          hasActiveFilters ? "filtered" : "total"
        } records successfully.`,
      );
    } catch (error) {
      console.error("Failed to export leave balances:", error);
      toast.error("Failed to export Excel file.");
    }
  };

  /* =======================================================
     DOWNLOAD SAMPLE FORMAT
  ======================================================= */

  const handleDownloadSampleFormat = () => {
    try {
      if (employeesLoading) {
        toast.info("Please wait while employees are loading.");
        return;
      }

      if (employees.length === 0) {
        toast.warning("No employees available to download.");
        return;
      }

      const headers = [
        "Employee ID",
        "Employee Name",
        "Organization",
        "EL",
        "CL",
        "SL",
      ];

      // Include all employees, leaving the annual balance columns blank.
      const templateRows = [...employees]
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            sensitivity: "base",
          }),
        )
        .map((employee) => ({
          "Employee ID": employee.id,
          "Employee Name": employee.name,
          Organization: employee.organization || "",
          EL: "",
          CL: "",
          SL: "",
        }));

      const worksheet = XLSX.utils.json_to_sheet(templateRows, {
        header: headers,
      });

      worksheet["!cols"] = [
        { wch: 16 },
        { wch: 30 },
        { wch: 25 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Leave Balance Import");

      const year = String(formik.values.year || CURRENT_YEAR);

      XLSX.writeFile(workbook, `Leave_Balance_Import_Template_${year}.xlsx`);

      toast.success(
        `Import template downloaded with ${employees.length} employees. Fill EL, CL and SL with the annual opening balances for the selected year.`,
      );
    } catch (error) {
      console.error("Failed to download sample format:", error);

      toast.error("Failed to download the sample format.");
    }
  };

  /* =======================================================
     YEARLY EXCEL IMPORT
  ======================================================= */

  const handleImportExcel = async (event) => {
    // Keep a direct reference because currentTarget may no longer be available
    // after the asynchronous import starts.
    const inputElement = event.currentTarget;
    const file = inputElement?.files?.[0];

    if (!file) {
      return;
    }

    // Prevent duplicate imports from starting before React renders the
    // disabled button state.
    if (importInProgressRef.current) {
      toast.info("An Excel import is already running.");
      inputElement.value = "";
      return;
    }

    importInProgressRef.current = true;

    const importIssues = [];
    let importedCount = 0;

    const addImportIssue = (rowNumber, employeeId, employeeName, issue) => {
      importIssues.push({
        rowNumber: rowNumber ?? "",
        employeeId: employeeId ?? "",
        employeeName: employeeName ?? "",
        issue: String(issue ?? "Unknown import issue"),
      });
    };

    const normalizeEmployeeId = (value) => {
      if (value === null || value === undefined) {
        return "";
      }

      return String(value)
        .replace(/\u00a0/g, " ")
        .trim()
        .replace(/,/g, "")
        .replace(/\.0+$/, "");
    };

    const normalizeHeader = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const downloadImportIssues = (issues, month, year) => {
      if (!issues.length) {
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(
        issues.map((item) => ({
          "Excel Row": item.rowNumber,
          "Employee ID": item.employeeId,
          "Employee Name": item.employeeName,
          Issue: item.issue,
        })),
      );

      worksheet["!cols"] = [{ wch: 12 }, { wch: 16 }, { wch: 30 }, { wch: 90 }];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Import Issues");

      const safeMonth = String(month || "Month").replace(/[^a-zA-Z0-9_-]/g, "");

      try {
        XLSX.writeFile(
          workbook,
          `Leave_Import_Issues_${safeMonth}_${year}.xlsx`,
        );
      } catch (error) {
        console.error("Failed to download the import issue report:", error);

        toast.error(
          "Import issue report could not be downloaded. Check the browser console for details.",
        );
      }
    };

    try {
      if (employeesLoading || balancesLoading) {
        toast.info("Please wait while employees and leave balances load.");
        return;
      }

      if (formik.isSubmitting) {
        toast.warning("Please wait for the current save operation to finish.");
        return;
      }

      if (editId !== null) {
        toast.warning(
          "Please finish editing or cancel the current edit before importing.",
        );
        return;
      }

      // Annual import always initializes January for the selected year.
      const periodYear = Number(formik.values.year);
      const issuePeriodLabel = "Annual";

      if (
        !Number.isInteger(periodYear) ||
        periodYear < 1900 ||
        periodYear > 9999
      ) {
        toast.error("Please select a valid year before importing.");
        return;
      }

      const validExtensions = [".xlsx", ".xls", ".csv"];
      const lowerFileName = file.name.toLowerCase();

      if (
        !validExtensions.some((extension) => lowerFileName.endsWith(extension))
      ) {
        toast.error("Please select an Excel (.xlsx, .xls) or CSV file.");
        return;
      }

      setIsImporting(true);
      toast.info(`Reading annual opening balances for ${periodYear}...`);

      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      if (!workbook.SheetNames?.length) {
        throw new Error("The selected Excel file contains no worksheets.");
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      const sheetRows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
        raw: true,
        blankrows: false,
      });

      if (!sheetRows.length) {
        throw new Error("The selected file is empty.");
      }

      const findColumnIndex = (headers, aliases) =>
        headers.findIndex((header) => aliases.includes(header));

      const employeeIdAliases = [
        "employeeid",
        "empid",
        "personid",
        "userid",
        "employeecode",
        "empcode",
      ];

      const elAliases = [
        "el",
        "usedel",
        "elused",
        "openingel",
        "elopening",
        "annualel",
        "elbalance",
      ];

      const clAliases = [
        "cl",
        "usedcl",
        "clused",
        "openingcl",
        "clopening",
        "annualcl",
        "clbalance",
      ];

      const slAliases = [
        "sl",
        "usedsl",
        "slused",
        "openingsl",
        "slopening",
        "annualsl",
        "slbalance",
      ];

      // Accept headers directly, or a title row above the headers.
      let headerRowIndex = -1;
      let employeeIdIndex = -1;
      let elIndex = -1;
      let clIndex = -1;
      let slIndex = -1;

      for (let index = 0; index < Math.min(sheetRows.length, 10); index += 1) {
        const candidateHeaders = sheetRows[index].map(normalizeHeader);

        const candidateEmployeeIdIndex = findColumnIndex(
          candidateHeaders,
          employeeIdAliases,
        );

        const candidateElIndex = findColumnIndex(candidateHeaders, elAliases);

        const candidateClIndex = findColumnIndex(candidateHeaders, clAliases);

        const candidateSlIndex = findColumnIndex(candidateHeaders, slAliases);

        if (
          candidateEmployeeIdIndex !== -1 &&
          candidateElIndex !== -1 &&
          candidateClIndex !== -1 &&
          candidateSlIndex !== -1
        ) {
          headerRowIndex = index;
          employeeIdIndex = candidateEmployeeIdIndex;
          elIndex = candidateElIndex;
          clIndex = candidateClIndex;
          slIndex = candidateSlIndex;
          break;
        }
      }

      if (headerRowIndex === -1) {
        throw new Error(
          "Invalid Excel format. Required columns: Employee ID, EL, CL and SL.",
        );
      }

      const parseLeaveAmount = (
        value,
        rowNumber,
        employeeId,
        employeeName,
        leaveType,
      ) => {
        const text = String(value ?? "").trim();

        // A blank individual leave column means 0, but completely blank
        // EL/CL/SL rows are skipped so an untouched template cannot reset leave.
        if (text === "") {
          return 0;
        }

        const normalizedText = text.replace(/,/g, "");
        const number = Number(normalizedText);

        if (!Number.isFinite(number) || number < 0) {
          addImportIssue(
            rowNumber,
            employeeId,
            employeeName,
            `${leaveType} must be a non-negative number. Received: "${text}".`,
          );

          return null;
        }

        const decimalPart = normalizedText.split(".")[1];

        if (decimalPart && decimalPart.length > 2) {
          addImportIssue(
            rowNumber,
            employeeId,
            employeeName,
            `${leaveType} can contain at most two decimal places.`,
          );

          return null;
        }

        return number;
      };

      const employeeMap = new Map(
        employees.map((employee) => [
          normalizeEmployeeId(employee.id),
          employee,
        ]),
      );

      const importedRows = [];
      const seenEmployeeIds = new Set();
      const importDataRows = sheetRows.slice(headerRowIndex + 1);

      importDataRows.forEach((cells, index) => {
        const rowNumber = headerRowIndex + index + 2;

        const employeeId = normalizeEmployeeId(cells[employeeIdIndex]);

        const rawEL = cells[elIndex];
        const rawCL = cells[clIndex];
        const rawSL = cells[slIndex];

        const hasAnyLeaveValue = [rawEL, rawCL, rawSL].some(
          (value) => String(value ?? "").trim() !== "",
        );

        const rowHasAnyValue = cells.some(
          (value) => String(value ?? "").trim() !== "",
        );

        if (!rowHasAnyValue) {
          return;
        }

        // Ignore template rows where HR has not entered any leave values.
        if (!hasAnyLeaveValue) {
          return;
        }

        if (!employeeId) {
          addImportIssue(
            rowNumber,
            "",
            "",
            "Employee ID is required when EL, CL or SL contains a value.",
          );

          return;
        }

        if (!/^\d+$/.test(employeeId) || Number(employeeId) <= 0) {
          addImportIssue(
            rowNumber,
            employeeId,
            "",
            "Employee ID must be a positive numeric ID.",
          );

          return;
        }

        if (seenEmployeeIds.has(employeeId)) {
          addImportIssue(
            rowNumber,
            employeeId,
            employeeMap.get(employeeId)?.name ?? "",
            "Duplicate Employee ID in the uploaded file. Only the first row is considered.",
          );

          return;
        }

        seenEmployeeIds.add(employeeId);

        const employee = employeeMap.get(employeeId);
        const employeeName = employee?.name ?? "";
        const issuesBeforeParsing = importIssues.length;

        const openingEL = parseLeaveAmount(
          rawEL,
          rowNumber,
          employeeId,
          employeeName,
          "EL",
        );

        const openingCL = parseLeaveAmount(
          rawCL,
          rowNumber,
          employeeId,
          employeeName,
          "CL",
        );

        const openingSL = parseLeaveAmount(
          rawSL,
          rowNumber,
          employeeId,
          employeeName,
          "SL",
        );

        if (
          importIssues.length !== issuesBeforeParsing ||
          openingEL === null ||
          openingCL === null ||
          openingSL === null
        ) {
          return;
        }

        importedRows.push({
          rowNumber,
          employeeId,
          employeeName,
          openingEL,
          openingCL,
          openingSL,
        });
      });

      if (importedRows.length === 0) {
        if (importIssues.length) {
          downloadImportIssues(importIssues, issuePeriodLabel, periodYear);

          toast.warning(
            `No valid rows were imported. ${importIssues.length} issue(s) were written to an Excel report.`,
          );
        } else {
          toast.warning("No employee data rows were found in the Excel file.");
        }

        return;
      }

      // SQL remains responsible for validating employee IDs and import rules.
      const tasks = importedRows.map((row) => {
        const employee = employeeMap.get(row.employeeId);

        if (employee) {
          row.employeeName = employee.name;
        }

        return row;
      });

      if (tasks.length === 0) {
        if (importIssues.length) {
          downloadImportIssues(importIssues, issuePeriodLabel, periodYear);

          toast.warning(
            `Nothing was submitted. ${importIssues.length} issue(s) were written to an Excel report.`,
          );
        } else {
          toast.warning("No importable employee rows were found.");
        }

        return;
      }

      // Use the in-app confirmation dialog instead of the browser's native confirm.
      // Keep this await here so the validated workbook data stays in scope until
      // the user confirms or cancels.
      const shouldContinue = await requestImportConfirmation(
        periodYear,
        tasks.length,
      );

      if (!shouldContinue) {
        toast.info("Excel import cancelled.");
        return;
      }

      // Use the yearly API contract and process sequential batches to stay
      // within the API's 5,000-row request limit.
      const totalBatches = Math.ceil(tasks.length / IMPORT_BATCH_SIZE);
      const resultRows = [];

      if (totalBatches > 1) {
        toast.info(
          `Importing ${tasks.length} employees in ${totalBatches} batches.`,
        );
      }

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex += 1) {
        const batch = tasks.slice(
          batchIndex * IMPORT_BATCH_SIZE,
          (batchIndex + 1) * IMPORT_BATCH_SIZE,
        );

        try {
          const response = await ImportLeaveBalanceYearly({
            year: periodYear,
            items: batch.map((row) => ({
              rowNumber: row.rowNumber,
              employeeId: Number(row.employeeId),
              openingEL: Number(row.openingEL),
              openingCL: Number(row.openingCL),
              openingSL: Number(row.openingSL),
            })),
          });

          const responseData = response?.data;

          const batchResultRows = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.data)
            ? responseData.data
            : Array.isArray(responseData?.result)
            ? responseData.result
            : [];

          if (batchResultRows.length === 0) {
            throw new Error(
              "The import API returned no row results for this batch.",
            );
          }

          resultRows.push(...batchResultRows);
        } catch (batchError) {
          const batchErrorMessage = getImportTopLevelError(batchError);

          // Record failed HTTP/SQL batches and continue with remaining batches.
          batch.forEach((row) => {
            addImportIssue(
              row.rowNumber,
              row.employeeId,
              row.employeeName,
              `Batch ${
                batchIndex + 1
              }/${totalBatches} failed: ${batchErrorMessage}`,
            );
          });
        }
      }

      importedCount = resultRows.filter(
        (row) =>
          String(row.status ?? row.Status ?? "").toUpperCase() === "SUCCESS",
      ).length;

      resultRows.forEach((row) => {
        const status = String(row.status ?? row.Status ?? "").toUpperCase();

        if (status === "SUCCESS") {
          return;
        }

        const employeeId = normalizeEmployeeId(
          row.employeeId ?? row.EmployeeId,
        );

        const employee = employeeMap.get(employeeId);

        addImportIssue(
          row.rowNumber ?? row.RowNumber,
          employeeId,
          employee?.name ?? row.employeeName ?? row.EmployeeName ?? "",
          row.message ?? row.Message ?? "The server rejected this row.",
        );
      });

      if (importedCount > 0) {
        await fetchLeaveBalances(employees);
      }

      if (importIssues.length > 0) {
        downloadImportIssues(importIssues, issuePeriodLabel, periodYear);
      }

      console.info("Leave balance import summary:", {
        importMode: "yearly",
        year: periodYear,
        processed: tasks.length,
        imported: importedCount,
        issues: importIssues.length,
      });

      const successDescription = `annual opening balances for ${periodYear}`;

      if (importIssues.length === 0) {
        toast.success(
          `Successfully imported ${importedCount} employee record(s): ${successDescription}.`,
        );
      } else if (importedCount === 0) {
        toast.error(
          `No records were imported. ${importIssues.length} issue(s) were downloaded in Leave_Import_Issues_${issuePeriodLabel}_${periodYear}.xlsx.`,
        );
      } else {
        toast.warning(
          `Import finished. Successful: ${importedCount}; issues: ${importIssues.length}. An Excel issue report was downloaded.`,
        );
      }
    } catch (error) {
      console.error("Failed to import annual leave balances:", error);
      toast.error(getImportTopLevelError(error));
    } finally {
      importInProgressRef.current = false;
      setIsImporting(false);

      // Clear the input so the same file can be selected again.
      if (inputElement) {
        inputElement.value = "";
      }
    }

    function getImportTopLevelError(error) {
      const errorData = error?.response?.data;

      const validationMessage = errorData?.errors
        ? Object.values(errorData.errors).flat().join(" ")
        : "";

      return (
        errorData?.message ||
        errorData?.Message ||
        errorData?.detail ||
        validationMessage ||
        (typeof errorData === "string" ? errorData : "") ||
        error?.message ||
        "Failed to import the Excel file."
      );
    }
  };

  /* =======================================================
     TABLE HEADER
  ======================================================= */

  const renderHeader = (label, field) => (
    <TableCell key={field}>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{ color: "text.primary" }}
      >
        <TableSortLabel
          active={orderBy === field}
          direction={orderBy === field ? order : "asc"}
          onClick={() => handleSort(field)}
        >
          {label}
        </TableSortLabel>
      </Box>
    </TableCell>
  );

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <PageContainer title="Leave Balance Master">
      <Breadcrumb title="Leave Balance Master" items={BCrumb} />

      <Box>
        {/* ADD / EDIT FORM */}

        <Card
          sx={{
            mb: 2,
            p: 0,
            overflow: "hidden",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            px={2}
            py={1.25}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <FeatherIcon icon="calendar" width="18" />

            <Box sx={{ ml: 1 }}>
              {editId !== null ? "Edit Leave Balance" : "Add Leave Balance"}
            </Box>
          </Box>

          <Box component="form" onSubmit={formik.handleSubmit}>
            <CardContent
              sx={{
                px: 2.5,
                py: 1.5,
                "&:last-child": {
                  pb: 2,
                },
              }}
            >
              {/* EMPLOYEE + PERIOD ON THE LEFT; BALANCE GROUPS ON THE RIGHT */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "minmax(245px, 1.05fr) repeat(3, minmax(180px, 1fr)) auto",
                  },
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 1,
                    alignContent: "center",
                    gridColumn: { xs: "1 / -1", sm: "1 / -1", lg: "auto" },
                  }}
                >
                  <Box sx={{ minWidth: 0, gridColumn: "1 / -1" }}>
                    <CustomFormLabel>Employee</CustomFormLabel>
                    <FormControl
                      fullWidth
                      size="small"
                      error={
                        formik.touched.employeeId &&
                        Boolean(formik.errors.employeeId)
                      }
                    >
                      <Select
                        id="employeeId"
                        name="employeeId"
                        value={formik.values.employeeId}
                        displayEmpty
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        onOpen={() => setEmployeeSearchTerm("")}
                        onClose={() => setEmployeeSearchTerm("")}
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <Typography
                                color="text.secondary"
                                sx={{ fontSize: 13 }}
                              >
                                {employeesLoading
                                  ? "Loading employees..."
                                  : "Select Employee"}
                              </Typography>
                            );
                          }
                          const employee = getSelectedEmployee(selected);
                          return employee
                            ? `${employee.name} (${employee.id})`
                            : String(selected);
                        }}
                        disabled={
                          employeesLoading || editId !== null || isImporting
                        }
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 340,
                              "& .MuiListSubheader-root": {
                                lineHeight: "normal",
                              },
                            },
                          },
                        }}
                        sx={{
                          height: 38,
                          backgroundColor: "#FFFFFF",
                          fontSize: 13,
                          "& .MuiSelect-select": { py: 1, pr: 3 },
                        }}
                      >
                        <MenuItem value="">Select Employee</MenuItem>
                        <ListSubheader
                          sx={{
                            backgroundColor: "#FFFFFF",
                            p: 1,
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder="Search name or ID..."
                            value={employeeSearchTerm}
                            onChange={(event) =>
                              setEmployeeSearchTerm(event.target.value)
                            }
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </ListSubheader>
                        {filteredEmployees.length === 0 ? (
                          <MenuItem disabled>No employees found</MenuItem>
                        ) : (
                          filteredEmployees.map((employee) => (
                            <MenuItem
                              key={employee.id}
                              value={employee.id}
                              sx={{ py: 1, fontSize: 13 }}
                            >
                              {employee.name} ({employee.id})
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {formik.touched.employeeId &&
                        formik.errors.employeeId && (
                          <FormHelperText>
                            {formik.errors.employeeId}
                          </FormHelperText>
                        )}
                    </FormControl>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <CustomFormLabel>Month</CustomFormLabel>
                    <CustomTextField
                      select
                      name="month"
                      value={formik.values.month}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      size="small"
                      disabled={editId !== null || isImporting}
                      error={
                        formik.touched.month && Boolean(formik.errors.month)
                      }
                      helperText={formik.touched.month && formik.errors.month}
                      sx={{
                        "& .MuiInputBase-root": { height: 38, fontSize: 13 },
                      }}
                    >
                      {MONTHS.map((month) => (
                        <MenuItem key={month} value={month}>
                          {month}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <CustomFormLabel>Year</CustomFormLabel>
                    <CustomTextField
                      select
                      name="year"
                      value={formik.values.year}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      size="small"
                      disabled={editId !== null || isImporting}
                      error={formik.touched.year && Boolean(formik.errors.year)}
                      helperText={formik.touched.year && formik.errors.year}
                      sx={{
                        "& .MuiInputBase-root": { height: 38, fontSize: 13 },
                      }}
                    >
                      {YEARS.map((year) => (
                        <MenuItem key={year} value={String(year)}>
                          {year}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Box>
                </Box>

                {BALANCE_GROUPS.map(renderBalanceGroup)}
              </Box>

              {/* ACTIONS ON THEIR OWN ROW, ALIGNED TO THE LEFT */}
              <Box
                sx={{
                  mt: 1.5,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.75,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  type="submit"
                  size="small"
                  disabled={
                    employeesLoading ||
                    balancesLoading ||
                    formik.isSubmitting ||
                    isImporting
                  }
                  sx={{ minWidth: 62, height: 34 }}
                >
                  {editId !== null ? "Update" : "Add"}
                </Button>
                <Button
                  variant="outlined"
                  type="button"
                  size="small"
                  onClick={resetForm}
                  disabled={isImporting}
                  sx={{ minWidth: 62, height: 34 }}
                >
                  {editId !== null ? "Cancel" : "Reset"}
                </Button>
              </Box>

              {previousBalance && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 1.5,
                  }}
                >
                  Opening balances were taken from {previousBalance.month}{" "}
                  {previousBalance.year}'s closing balances for this employee.
                </Typography>
              )}
            </CardContent>
          </Box>
        </Card>

        {/* EXISTING LEAVE BALANCES */}

        <Card
          sx={{
            mt: 2,
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* TABLE TITLE BAR */}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            px={2}
            py={1.5}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <Box display="flex" alignItems="center">
              <FeatherIcon icon="alert-circle" width="18" />

              <Box sx={{ ml: 1 }}>Existing Leave Balances</Box>
            </Box>

            {/* EXPORT, IMPORT, SEARCH, AND FILTER CONTROLS */}

            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              flexWrap="wrap"
              gap={1}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<FeatherIcon icon="download" width="16" />}
                sx={{ height: 32, whiteSpace: "nowrap" }}
                onClick={handleExportExcel}
                disabled={isImporting || balancesLoading || list.length === 0}
              >
                Export Excel
              </Button>

              <input
                ref={yearlyImportFileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleImportExcel}
                style={{ display: "none" }}
              />

              <Button
                variant="contained"
                size="small"
                startIcon={<FeatherIcon icon="calendar" width="16" />}
                sx={{ height: 32, whiteSpace: "nowrap" }}
                onClick={() => yearlyImportFileInputRef.current?.click()}
                disabled={
                  isImporting ||
                  employeesLoading ||
                  balancesLoading ||
                  editId !== null ||
                  formik.isSubmitting
                }
              >
                {isImporting ? "Importing..." : "Import Excel"}
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<FeatherIcon icon="file-text" width="16" />}
                sx={{ height: 32, whiteSpace: "nowrap" }}
                onClick={handleDownloadSampleFormat}
                disabled={employeesLoading || employees.length === 0}
              >
                Download Sample
              </Button>

              {/* SEARCH AND FILTERS */}

              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flexWrap="wrap"
                gap={1}
              >
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={globalSearch}
                  onFocus={() => {
                    setShowAllFilters(false);
                    setSearchText({});
                  }}
                  onChange={(event) => {
                    setGlobalSearch(event.target.value);
                    setPage(0);
                  }}
                  sx={{
                    width: { xs: "100%", sm: 190 },
                    backgroundColor: "white",
                    borderRadius: 1,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 1 }}>
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {globalSearch && (
                  <CloseIcon
                    fontSize="small"
                    sx={{ cursor: "pointer", ml: 0.25 }}
                    onClick={() => {
                      setGlobalSearch("");
                      setPage(0);
                    }}
                  />
                )}

                <Tooltip title="Column Filters">
                  <FilterAltIcon
                    sx={{
                      cursor: "pointer",
                      ml: 1,
                      color: showAllFilters
                        ? (theme) => theme.palette.primary.main
                        : "text.primary",
                      transition: "0.2s",
                    }}
                    onClick={() => setShowAllFilters((previous) => !previous)}
                  />
                </Tooltip>

                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1, height: 32, whiteSpace: "nowrap" }}
                  onClick={handleResetFilters}
                >
                  Reset Filter
                </Button>
              </Box>
            </Box>
          </Box>

          {/* TABLE CONTENT */}

          <CardContent sx={{ p: 2 }}>
            {balancesLoading ? (
              <Typography>Loading leave balances...</Typography>
            ) : list.length === 0 ? (
              <Typography>No Leave Balance Records Found</Typography>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    sx={{
                      minWidth: 1550,
                      "& .MuiTableCell-root": {
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#FAFBFB",
                          "& .MuiTableCell-root": {
                            position: "sticky",
                            top: 0,
                            zIndex: 4,
                            backgroundColor: "#FAFBFB",
                          },
                        }}
                      >
                        {renderHeader("Employee ID", "employeeId")}

                        {renderHeader("Employee Name", "employeeName")}

                        {renderHeader("Month", "month")}

                        {renderHeader("Year", "year")}

                        {BALANCE_COLUMNS.map((column) =>
                          renderHeader(column.label, column.field),
                        )}

                        <TableCell align="center">Action</TableCell>
                      </TableRow>

                      {/* COLUMN FILTER ROW */}

                      {showAllFilters && (
                        <TableRow
                          sx={{
                            backgroundColor: "#FFFFFF",
                            "& .MuiTableCell-root": {
                              padding: "6px 8px",
                              height: 42,
                              verticalAlign: "middle",
                            },
                          }}
                        >
                          {FILTER_FIELDS.map((field) => (
                            <TableCell key={field}>
                              <TextField
                                size="small"
                                fullWidth
                                value={searchText[field] || ""}
                                onChange={(event) => {
                                  setSearchText((previous) => ({
                                    ...previous,
                                    [field]: event.target.value,
                                  }));

                                  setPage(0);
                                }}
                                placeholder="Search..."
                                sx={{
                                  minWidth: 90,
                                  "& .MuiInputBase-root": {
                                    height: 32,
                                    fontSize: 13,
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "6px 8px",
                                  },
                                }}
                                InputProps={{
                                  endAdornment: searchText[field] ? (
                                    <InputAdornment position="end">
                                      <CloseIcon
                                        fontSize="small"
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => {
                                          setSearchText((previous) => ({
                                            ...previous,
                                            [field]: "",
                                          }));

                                          setPage(0);
                                        }}
                                      />
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            </TableCell>
                          ))}

                          <TableCell />
                        </TableRow>
                      )}
                    </TableHead>

                    <TableBody>
                      {(rowsPerPage > 0
                        ? sortedList.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                        : sortedList
                      ).map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>{item.employeeId || "-"}</TableCell>

                          <TableCell>{item.employeeName || "-"}</TableCell>

                          <TableCell>{item.month || "-"}</TableCell>

                          <TableCell>{item.year || "-"}</TableCell>

                          {BALANCE_COLUMNS.map((column) => (
                            <TableCell key={column.field}>
                              {formatBalance(item[column.field])}
                            </TableCell>
                          ))}

                          <TableCell align="center">
                            <Tooltip title="Edit">
                              <EditIcon
                                sx={{
                                  cursor: isImporting
                                    ? "not-allowed"
                                    : "pointer",
                                  color: isImporting
                                    ? "text.disabled"
                                    : "primary.main",
                                  opacity: isImporting ? 0.5 : 1,
                                }}
                                onClick={() => {
                                  if (!isImporting) {
                                    handleEdit(item);
                                  }
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredList.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={FILTER_FIELDS.length + 1}
                            align="center"
                          >
                            No data found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredList.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  showFirstButton
                  showLastButton
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={importConfirmation.open}
        onClose={() => resolveImportConfirmation(false)}
        aria-labelledby="annual-import-dialog-title"
        aria-describedby="annual-import-dialog-description"
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.22)",
          },
        }}
      >
        <DialogTitle
          id="annual-import-dialog-title"
          sx={{
            px: 3,
            py: 2.25,
            color: "primary.main",
            backgroundColor: "primary.light",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                borderRadius: 2,
                backgroundColor: "background.paper",
              }}
            >
              <FeatherIcon icon="upload-cloud" width="21" />
            </Box>
            <Box minWidth={0}>
              <Typography variant="h6" component="div" fontWeight={700}>
                Confirm Annual Import
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review the details before updating leave balances.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Typography
            id="annual-import-dialog-description"
            variant="body1"
            sx={{ mb: 2 }}
          >
            You are about to import annual opening balances. Please confirm that
            the selected year and employee count are correct.
          </Typography>

          <Grid container spacing={1.5} sx={{ mb: 2.25 }}>
            <Grid item xs={12} sm={5}>
              <Box
                sx={{
                  p: 1.75,
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Import year
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {importConfirmation.year}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={7}>
              <Box
                sx={{
                  p: 1.75,
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Employee rows to validate
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {importConfirmation.rowCount.toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              p: 1.75,
              borderRadius: 2,
              backgroundColor: "#FFF8E7",
              border: "1px solid #F5D58A",
              color: "#624B16",
            }}
          >
            <FeatherIcon icon="info" width="18" />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Before you continue
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                This initializes or updates January&apos;s annual opening
                balances. Existing January usage is preserved. December balances
                are not carried into the new year.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => resolveImportConfirmation(false)}
            sx={{ minWidth: 108 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => resolveImportConfirmation(true)}
            startIcon={<FeatherIcon icon="check-circle" width="16" />}
            sx={{ minWidth: 150 }}
          >
            Confirm &amp; Import
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default LeaveBalanceMaster;
